import type { Department } from '@clockwise/clients/departments';
import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { AuditEntryList } from '@/components/custom/audit/audit-entry-list';
import { BackLink } from '@/components/custom/back-link';
import { CatalogFormSheet } from '@/components/custom/catalog-form-sheet';
import {
  DetailList,
  type DetailListRow,
  DetailListSkeleton,
} from '@/components/custom/detail-list';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { RequirePermission } from '@/components/custom/require-permission';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermissionGuard } from '@/hooks/use-permission-guard';
import { auditApi, departmentsApi } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

const TAB_VALUES = ['details', 'audit'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const detailSearch = z.object({
  tab: z.enum(TAB_VALUES).optional(),
});

export const Route = createFileRoute('/_authenticated/setup/departments/$id/')({
  validateSearch: detailSearch,
  component: DepartmentDetailPage,
});

const AUDIT_PAGE_SIZE = 50;
const DEFAULT_TAB: TabValue = 'details';

// Single source for the detail rows: drives both the loaded table and the
// skeleton's row count, so the two can't drift (and the layout won't jump).
const DETAIL_FIELDS: { label: string; value: (d: Department) => ReactNode }[] = [
  { label: 'Code', value: (d) => <code className="font-mono text-sm">{d.code}</code> },
  { label: 'Name', value: (d) => d.name },
  { label: 'Status', value: (d) => <StatusBadge active={d.active} /> },
  { label: 'Created', value: (d) => new Date(d.createdAt).toLocaleString() },
  { label: 'Updated', value: (d) => new Date(d.updatedAt).toLocaleString() },
];

function DepartmentDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { allowed: canView, fallback } = usePermissionGuard('department.view', 'departments');
  // The Audit tab reads /api/audit, which is gated by `audit.view`. Without it
  // the tab is hidden entirely rather than rendering a "Forbidden." panel.
  const canViewAudit = useHasPermission('audit.view');
  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  const activeTab: TabValue =
    requestedTab === 'audit' && !canViewAudit ? DEFAULT_TAB : requestedTab;

  const detail = useQuery({
    queryKey: queryKeys.departments.detail(id),
    queryFn: () => departmentsApi.get(id),
    enabled: canView,
  });

  const audit = useQuery({
    queryKey: queryKeys.audit.forEntity({
      entityType: 'departments',
      entityId: id,
      limit: AUDIT_PAGE_SIZE,
      offset: 0,
    }),
    queryFn: () =>
      auditApi.list({
        entityType: 'departments',
        entityId: id,
        limit: AUDIT_PAGE_SIZE,
        offset: 0,
      }),
    enabled: canView && canViewAudit,
  });

  const department = detail.data?.data ?? null;
  const [editOpen, setEditOpen] = useState(false);

  // Status changes are audited, so refresh both the list/detail caches and the
  // Audit tab shown on this page.
  const invalidateAfterStatusChange = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.departments.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };

  const deactivateMutation = useMutation({
    mutationFn: () => departmentsApi.remove(id),
    onSuccess: () => {
      toast.success('Deactivated');
      invalidateAfterStatusChange();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Deactivate failed'),
  });

  const restoreMutation = useMutation({
    mutationFn: () => departmentsApi.restore(id),
    onSuccess: () => {
      toast.success('Reactivated');
      invalidateAfterStatusChange();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Restore failed'),
  });

  const statusBusy = deactivateMutation.isPending || restoreMutation.isPending;

  if (fallback) return fallback;

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/setup/departments">Back to departments</BackLink>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          {detail.isLoading ? (
            <>
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-40" />
            </>
          ) : detail.isError ? (
            <h1 className="text-2xl font-semibold text-destructive">
              {detail.error instanceof ApiError ? detail.error.message : 'Failed to load.'}
            </h1>
          ) : department ? (
            <>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold">{department.name}</h1>
                <StatusBadge active={department.active} />
              </div>
              <p className="text-sm text-muted-foreground">
                <code className="rounded bg-muted px-1 py-0.5 text-xs">{department.code}</code>
              </p>
            </>
          ) : (
            <h1 className="text-2xl font-semibold">Not found</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <RequirePermission permission="department.edit">
            {department?.active ? (
              <Button
                variant="outline"
                disabled={statusBusy}
                onClick={() => {
                  if (confirm(`Deactivate "${department.name}"?`)) {
                    deactivateMutation.mutate();
                  }
                }}
              >
                Deactivate
              </Button>
            ) : department && !department.active ? (
              <Button
                variant="outline"
                disabled={statusBusy}
                onClick={() => restoreMutation.mutate()}
              >
                Reactivate
              </Button>
            ) : null}
            <Button disabled={!department} onClick={() => setEditOpen(true)}>
              <Pencil />
              Edit
            </Button>
          </RequirePermission>
        </div>
      </header>

      <PillTabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            search: (s) => ({ ...s, tab: v === DEFAULT_TAB ? undefined : (v as TabValue) }),
            replace: true,
          })
        }
        className="rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="details">Details</PillTabsTrigger>
          {canViewAudit ? (
            <PillTabsTrigger value="audit">
              Audit
              {audit.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {audit.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
        </PillTabsList>

        <PillTabsContent value="details" className="space-y-3">
          <div className="surface-card rounded-md border">
            {detail.isLoading ? (
              <DetailListSkeleton rows={DETAIL_FIELDS.length} />
            ) : department ? (
              <DetailList rows={detailRows(department)} />
            ) : (
              <p className="p-4 text-sm text-muted-foreground">Department not found.</p>
            )}
          </div>
        </PillTabsContent>

        <PillTabsContent value="audit" className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Every state-changing operation, newest first.
          </p>
          <div className="rounded-md border">
            {audit.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : audit.isError ? (
              <p className="p-4 text-sm text-destructive">
                {audit.error instanceof ApiError
                  ? audit.error.message
                  : 'Failed to load audit log.'}
              </p>
            ) : (
              <AuditEntryList items={audit.data?.data ?? []} total={audit.data?.meta.total ?? 0} />
            )}
          </div>
        </PillTabsContent>
      </PillTabs>

      <CatalogFormSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        entityLabel="department"
        entity={department}
        codeField={{ label: 'Code', placeholder: 'ENG' }}
        showDescription={false}
        submit={(body) => {
          if (!department) throw new Error('Department not loaded');
          if (!body.code) {
            throw new Error('code missing — CatalogFormSheet contract violation');
          }
          return departmentsApi.patch(department.id, { name: body.name, code: body.code });
        }}
        invalidateKeys={[
          queryKeys.departments.all,
          queryKeys.audit.all,
          queryKeys.departments.detail(id),
        ]}
      />
    </PageShell>
  );
}

function detailRows(department: Department): DetailListRow[] {
  return DETAIL_FIELDS.map((f) => ({ label: f.label, value: f.value(department) }));
}

function StatusBadge({ active }: { active: boolean }) {
  if (active) {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      Inactive
    </span>
  );
}
